import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getCountry } from "@store/selectors/country";
import { getUsCountryId } from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";

import { createOrder } from "@actions/order";
import { updateCart } from "@slices/cart";

import { useSendItemsContext } from "@contexts/send-items-context";

import { isGlobalCountry } from "@stadium/helpers/country/utils";

import { getIsLockerShop, getStoreExternalId } from "@store/selectors/store";

import { SM_PROGRAM_ID } from "@constants/app-defaults";
import { ORDER_TYPES, SWAG_PURCHASED_FROM_US_INTENT } from "@constants/orders/cart";

const useFindOrCreateDraftOrder = ({ type = ORDER_TYPES.organizer } = {}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const selectedCountry = useSelector(getCountry);
  const storeExternalId = useSelector(getStoreExternalId);
  const usCountryId = useConfig(getUsCountryId);
  const isLockerShop = useSelector(getIsLockerShop);

  const { isSendToMyself } = useSendItemsContext();

  const postOrderFetch = useCallback(
    (order) => {
      dispatch(updateCart(order));
      setLoading(false);
      return order;
    },
    [dispatch, setLoading],
  );

  const findOrCreateDraftOrder = useCallback(() => {
    const isGlobal = isGlobalCountry(selectedCountry);
    if (storeExternalId && selectedCountry.id) {
      const payload = {
        program_id: SM_PROGRAM_ID,
        swag_store_id: storeExternalId,
        eshop: true,
        find_existing_self_order: true,
        country_id: isGlobal ? usCountryId : selectedCountry.id,
        type,
        send_to_many: !isSendToMyself,
        ...(!isSendToMyself && {
          gift_detail_attributes: {},
          treat_name: "",
        }),
        ...(isLockerShop && { program_intent: SWAG_PURCHASED_FROM_US_INTENT }),
      };
      setLoading(true);
      return dispatch(createOrder(payload))
        .then(postOrderFetch)
        .catch((err) => {
          setLoading(false);
          throw err;
        });
    }
  }, [storeExternalId, selectedCountry, dispatch, postOrderFetch]);

  return {
    loading,
    findOrCreateDraftOrder,
  };
};

export default useFindOrCreateDraftOrder;
